import { IPageSettings } from "../../interfaces/IPageSettings";

export class DestinationUrlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DestinationUrlError";
    Object.setPrototypeOf(this, DestinationUrlError.prototype);
  }
}

export type ResolvedDestinationSource =
  | "graph-item"
  | "graph-drive-root"
  | "same-origin-url"
  | "server-relative"
  | "concat";

export interface IResolvedDestination {
  absoluteUrl: string;
  source: ResolvedDestinationSource;
}

type DestinationDirectory = Pick<IPageSettings, "webUrl" | "directory" | "isTeamsRootFolder" | "parentId" | "id">;

export interface ResolveDestinationUrlOptions {
  isOneDrive?: boolean;
  getDriveRootWebUrlFn?: (driveId: string) => Promise<string>;
  getDriveItemWebUrlFn?: (driveId: string, driveItemId: string) => Promise<string>;
}

export const buildDestinationUrl = (webUrl?: string, directory?: string): string => {
  const normalizedWebUrl = webUrl?.trim().replace(/\/+$/, "");
  const normalizedDirectory = directory?.trim().replace(/^\/+/, "");

  if (!normalizedWebUrl) {
    return "";
  }

  return normalizedDirectory
    ? `${normalizedWebUrl}/${normalizedDirectory}`
    : normalizedWebUrl;
};

const SERVER_RELATIVE_PREFIX = /^\/(?:sites|personal|teams|drives)\//i;
const HTTP_PREFIX = /^https?:\/\//i;

const stripTrailingSlashes = (value: string): string => value.replace(/\/+$/, "");

const decodeUrlPath = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const sameOrigin = (a: string, b: string): boolean => {
  try {
    return new URL(a).origin === new URL(b).origin;
  } catch {
    return false;
  }
};

const assertSameOrigin = (candidate: string, baseWebUrl: string, source: ResolvedDestinationSource): void => {
  if (!sameOrigin(candidate, baseWebUrl)) {
    throw new DestinationUrlError(
      `Destination origin differs from base webUrl origin (source=${source})`
    );
  }
};

export const resolveDestination = async (
  destinationDirectory?: DestinationDirectory,
  options?: ResolveDestinationUrlOptions
): Promise<IResolvedDestination> => {
  if (!destinationDirectory) {
    return { absoluteUrl: "", source: "concat" };
  }

  const webUrl = destinationDirectory.webUrl?.trim();
  if (!webUrl) {
    return { absoluteUrl: "", source: "concat" };
  }
  if (!HTTP_PREFIX.test(webUrl)) {
    throw new DestinationUrlError("Destination webUrl must be an absolute http(s) URL");
  }

  let baseOrigin: string;
  try {
    baseOrigin = new URL(webUrl).origin;
  } catch {
    throw new DestinationUrlError("Destination webUrl could not be parsed as a URL");
  }

  const directoryValue = destinationDirectory.directory?.trim();

  // Level 2: Graph canonical preference.
  // Teams root folders with a null parentId: resolve against the channel's drive root.
  // Any other directory with parentId + id: resolve against the drive item's webUrl.
  const isTeamsRootLibraryRoot = destinationDirectory.isTeamsRootFolder === true
    && !destinationDirectory.parentId
    && Boolean(destinationDirectory.id);

  if (options?.isOneDrive !== true && isTeamsRootLibraryRoot) {
    try {
      const resolver = options?.getDriveRootWebUrlFn ?? getDefaultDriveRootWebUrlResolver();
      const driveRootUrl = await resolver(destinationDirectory.id as string);
      if (driveRootUrl) {
        assertSameOrigin(driveRootUrl, webUrl, "graph-drive-root");
        return { absoluteUrl: stripTrailingSlashes(driveRootUrl), source: "graph-drive-root" };
      }
    } catch (error) {
      if (error instanceof DestinationUrlError) {
        throw error;
      }
      // Fall through to concat fallback on transient Graph failures.
    }
  } else if (destinationDirectory.parentId && destinationDirectory.id && destinationDirectory.isTeamsRootFolder !== true) {
    try {
      const resolver = options?.getDriveItemWebUrlFn ?? getDefaultDriveItemWebUrlResolver();
      const itemWebUrl = await resolver(destinationDirectory.parentId, destinationDirectory.id);
      if (itemWebUrl) {
        assertSameOrigin(itemWebUrl, webUrl, "graph-item");
        return { absoluteUrl: stripTrailingSlashes(itemWebUrl), source: "graph-item" };
      }
    } catch (error) {
      if (error instanceof DestinationUrlError) {
        throw error;
      }
      // Fall through to concat fallback on transient Graph failures.
    }
  }

  // Level 1: defensive concat fallback.
  if (directoryValue && HTTP_PREFIX.test(directoryValue)) {
    let directoryAsUrl: URL;
    try {
      directoryAsUrl = new URL(directoryValue);
    } catch {
      throw new DestinationUrlError("Destination directory is not a parseable URL");
    }
    if (directoryAsUrl.origin !== baseOrigin) {
      throw new DestinationUrlError(
        "Destination directory origin differs from webUrl origin"
      );
    }
    return {
      absoluteUrl: `${directoryAsUrl.origin}${stripTrailingSlashes(decodeUrlPath(directoryAsUrl.pathname))}`,
      source: "same-origin-url",
    };
  }

  if (directoryValue && SERVER_RELATIVE_PREFIX.test(directoryValue)) {
    return {
      absoluteUrl: `${baseOrigin}${stripTrailingSlashes(directoryValue)}`,
      source: "server-relative",
    };
  }

  const concat = buildDestinationUrl(webUrl, directoryValue);
  return { absoluteUrl: concat, source: "concat" };
};

export const resolveDestinationUrl = async (
  destinationDirectory?: DestinationDirectory,
  options?: ResolveDestinationUrlOptions
): Promise<string> => {
  const resolved = await resolveDestination(destinationDirectory, options);
  return resolved.absoluteUrl;
};

// -------- default Graph resolvers (lazy-required so tests can run in Node without browser deps) --------

let defaultDriveRootWebUrlResolver: ((driveId: string) => Promise<string>) | undefined;

const getDefaultDriveRootWebUrlResolver = (): ((driveId: string) => Promise<string>) => {
  if (defaultDriveRootWebUrlResolver) {
    return defaultDriveRootWebUrlResolver;
  }

  const { getDriveRootWebUrl } = require("../../services/GraphService");
  defaultDriveRootWebUrlResolver = getDriveRootWebUrl;

  return defaultDriveRootWebUrlResolver;
};

let defaultDriveItemWebUrlResolver: ((driveId: string, driveItemId: string) => Promise<string>) | undefined;
const inFlightDriveItemWebUrlRequests = new Map<string, Promise<string | undefined>>();

const getDefaultDriveItemWebUrlResolver = (): ((driveId: string, driveItemId: string) => Promise<string>) => {
  if (defaultDriveItemWebUrlResolver) {
    return defaultDriveItemWebUrlResolver;
  }

  const { getItemWebViewUrl } = require("../../services/GraphService");
  const authModuleExports = require("../../../../utilities/AuthModule");
  const authModule = authModuleExports.default ?? authModuleExports;

  defaultDriveItemWebUrlResolver = async (driveId: string, driveItemId: string): Promise<string> => {
    const cacheKey = `${driveId}|${driveItemId}`;
    let inFlight = inFlightDriveItemWebUrlRequests.get(cacheKey);
    if (!inFlight) {
      inFlight = (async () => {
        try {
          const token = await authModule.getGraphAccessToken();
          const url = await getItemWebViewUrl(token, driveId, driveItemId);
          return url ?? undefined;
        } catch {
          return undefined;
        }
      })().finally(() => {
        inFlightDriveItemWebUrlRequests.delete(cacheKey);
      });
      inFlightDriveItemWebUrlRequests.set(cacheKey, inFlight);
    }
    const resolved = await inFlight;
    return resolved as string;
  };

  return defaultDriveItemWebUrlResolver;
};
